import { formatDate } from '../../format';

/**
 * What to say when the Receita Federal disagrees with an inclusão.
 *
 * One module for two screens on purpose. The same refusal reaches a customer
 * through the spreadsheet preview and through the Alterações form, and a person
 * who files the same inclusão both ways must not be told two different things
 * about it. Writing the sentence twice is how that happens.
 *
 * **The sentence is composed from values, never from the server's prose.** The
 * API's message is English and is written for whoever is integrating; what
 * crosses the boundary is the stable code (`identity_mismatch`) and the
 * registry's own value per field -- `rejection_context` on an upload row,
 * `details[].registry_value` on the error envelope. Both arrive here as the
 * same map.
 *
 * **The value is quoted back because the customer cannot see it any other way.**
 * This reverses the product's earlier decision to name the field and stop. An
 * accent, a married surname or a pair of transposed digits is invisible to
 * somebody rereading their own spreadsheet, so "o nome não confere" sent them
 * round the loop again. Whoever reads this sentence is an authenticated user of
 * the company, acting on that company's contract, about a person whose full
 * data they have just typed from that person's paperwork -- and the attempt is
 * in the audit trail.
 */

/** What the registry holds, keyed by the API's own name for the field. */
export type RegistryValues = Readonly<Record<string, string>>;

/** The API's field names, in the order a sentence lists them. */
const FIELD_ORDER = ['cpf', 'full_name', 'birth_date'] as const;

/** How each field starts a sentence: "O nome não confere". */
const SUBJECTS: Record<string, string> = {
  cpf: 'O CPF',
  full_name: 'O nome',
  birth_date: 'A data de nascimento',
};

/** How each field is named mid-sentence, when two of them are being listed. */
const NOUNS: Record<string, string> = {
  cpf: 'o CPF',
  full_name: 'o nome',
  birth_date: 'a data de nascimento',
};

/**
 * A registered value as a Brazilian reads it.
 *
 * A name is quoted, because a name with a space in it needs a boundary and
 * because quotation marks are what say "this is the spelling, character for
 * character". A birth date arrives as ISO 8601 and is shown dd/mm/yyyy, which
 * is the one format this product renders a date in -- `03/04/2026` meaning the
 * third of April is a coverage error, not a cosmetic one.
 */
function readable(field: string, value: string): string {
  return field === 'birth_date' ? formatDate(value) : `"${value}"`;
}

function joined(parts: readonly string[]): string {
  if (parts.length <= 1) {
    return parts[0] ?? '';
  }
  return `${parts.slice(0, -1).join(', ')} e ${parts[parts.length - 1]}`;
}

/** What a CPF nobody has ever been issued says. No value, because there is none. */
export const UNREGISTERED_CPF_MESSAGE =
  'Este CPF não está registrado na Receita Federal. Confira o número no cartão de CPF da pessoa e envie de novo.';

/**
 * The whole refusal as one sentence, for a banner or a spreadsheet cell.
 *
 * With no values this is the unregistered CPF: the registry answered and holds
 * no such number, so there is nothing to quote and nothing is invented.
 */
export function identityMismatchMessage(values: RegistryValues): string {
  const fields = FIELD_ORDER.filter((field) => values[field] !== undefined);
  if (fields.length === 0) {
    return UNREGISTERED_CPF_MESSAGE;
  }

  const subject = joined(
    fields.map((field, index) => (index === 0 ? SUBJECTS : NOUNS)[field] ?? field),
  );
  const verb = fields.length > 1 ? 'não conferem' : 'não confere';
  // One field needs no label -- the subject just said which it is. Two do, or
  // the reader has to guess which value belongs to which field.
  const held = joined(
    fields.map((field) => {
      const value = readable(field, values[field] as string);
      return fields.length > 1 ? `${NOUNS[field] ?? field} ${value}` : value;
    }),
  );

  return `${subject} ${verb}: a Receita Federal tem registrado ${held} para este CPF. Corrija e envie de novo.`;
}

/**
 * One field's correction, for the input that field is typed into.
 *
 * Shorter than {@link identityMismatchMessage} and deliberately so: it appears
 * under the input, where "o nome não confere" is already implied by which input
 * it is under.
 */
export function identityMismatchFieldMessage(field: string, value: string | undefined): string {
  if (value === undefined) {
    return field === 'cpf'
      ? UNREGISTERED_CPF_MESSAGE
      : 'Este dado não confere com o que a Receita Federal tem registrado para este CPF.';
  }
  return `A Receita Federal tem ${readable(field, value)} registrado para este CPF.`;
}
